import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnrollModal = ({ course, onClose }) => {
  const [step, setStep] = useState("details");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);

  useEffect(() => {
    if (course.price > 0) {
      axios.get(`${API}/paypal/config`).then((r) => setPaypalConfig(r.data)).catch(() => {});
    }
  }, [course]);

  const submitDetails = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enrollments`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        course_id: course.id,
        course_title: course.title,
      });
      if (course.price > 0) {
        setEnrollment(data.enrollment);
        setStep("pay");
      } else {
        toast.success("You're enrolled", {
          description: `Free access confirmed for "${course.title}". Check your inbox.`,
        });
        onClose();
      }
    } catch (err) {
      toast.error("Something went wrong", { description: "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  const paypalCreateOrder = async () => {
    const { data } = await axios.post(`${API}/paypal/orders`, { enrollment_id: enrollment.id });
    return data.order_id;
  };

  const paypalApprove = async (data) => {
    try {
      const resp = await axios.post(`${API}/paypal/orders/${data.orderID}/capture`);
      if (resp.data.status === "COMPLETED") {
        setStep("done");
      } else {
        toast.error("Payment not completed", { description: "PayPal did not confirm the payment." });
      }
    } catch (err) {
      toast.error("PayPal payment failed", { description: "Please try again." });
    }
  };

  const inputCls =
    "w-full bg-[#ffffff] border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 transition-colors";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="enroll-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.97 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7 shadow-2xl"
          data-testid="enroll-modal"
        >
          <div className="flex items-start justify-between mb-1">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600">
              {step === "details" ? "Enroll" : step === "pay" ? "Payment" : "Confirmed"}
            </p>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors" data-testid="enroll-modal-close" aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight leading-snug pr-6">{course.title}</h3>
          <div className="mt-3 flex items-center gap-3">
            <span className="font-mono2 text-2xl font-bold text-amber-600">{course.price === 0 ? "FREE" : `$${course.price.toLocaleString()}`}</span>
            {course.original_price && <span className="font-mono2 text-sm text-slate-500 line-through">${course.original_price.toLocaleString()}</span>}
            <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{course.duration}</span>
          </div>

          {step === "details" && (
            <form onSubmit={submitDetails} className="mt-6 space-y-3.5">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className={inputCls}
                data-testid="enroll-form-name-input"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                className={inputCls}
                data-testid="enroll-form-email-input"
              />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone / WhatsApp (optional)"
                className={inputCls}
                data-testid="enroll-form-phone-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-amber-500 disabled:opacity-60 transition-colors duration-300"
                data-testid="enroll-form-submit-button"
              >
                {loading && <Loader2 size={15} className="animate-spin" />}
                {loading ? "Processing..." : course.price > 0 ? "Continue to Payment" : "Enroll Free"}
              </button>
              <p className="text-center text-[11px] text-slate-500">
                {course.price > 0 ? "Secure payment via PayPal on the next step." : "Instant free access — no payment required."}
              </p>
            </form>
          )}

          {step === "pay" && enrollment && (
            <div className="mt-6 space-y-4" data-testid="enroll-payment-step">
              {paypalConfig?.client_id ? (
                <div className="relative z-0" data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: "USD", intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay", height: 45 }}
                      forceReRender={[course.price]}
                      createOrder={paypalCreateOrder}
                      onApprove={paypalApprove}
                      onError={() => toast.error("PayPal error", { description: "Please try again." })}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-6 text-slate-500 text-sm">
                  <Loader2 size={15} className="animate-spin" /> Loading secure payment...
                </div>
              )}
              <p className="text-center text-[11px] text-slate-500">
                Pay ${course.price.toLocaleString()} securely with PayPal — seat confirms instantly after payment.
              </p>
            </div>
          )}

          {step === "done" && (
            <div className="mt-6 text-center py-6" data-testid="enroll-payment-success">
              <CheckCircle2 size={44} className="mx-auto text-emerald-600 mb-5" />
              <h4 className="font-display text-lg font-bold mb-2">Payment confirmed</h4>
              <p className="text-sm text-slate-600">Your seat for this course is booked. Joining details are on the way to your inbox.</p>
              <button
                onClick={onClose}
                className="mt-6 bg-amber-400 text-slate-900 font-semibold text-sm px-8 py-3 rounded-full hover:bg-amber-500 transition-colors"
                data-testid="enroll-done-close"
              >
                Done
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EnrollModal;
